import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import type { AgentState, AgentStep } from '#/index'
import { NAV_TITLES } from '#/index'
import { useScrollControl } from '@/hooks/useScrollControl'
import Loading from '@/components/Loading'
import ErrorStateComponent from '@/components/ErrorState'
import Navigation from '@/components/Navigation'
import BackToTop from '@/components/BackToTop'
import StepNavigation from '@/components/StepNavigation'
import placeholderIllust from '@/assets/images/placeholder-illust.svg'
import iconChat from '@/assets/images/icons/icon-chat.svg'
import './index.scss'

interface ResultAreaProps {
  agents: AgentState[]
  activeViewStep: AgentStep
  onUserStepClick: (step: AgentStep) => void
  isGenerating: boolean
  quotedInput: string
  fullInput: string
  onRetry: () => void
}

/** 为 PRD 内容（Agent 2）插入锚点 */
function insertAnchors(content: string): string {
  return NAV_TITLES.reduce((result, title, index) => {
    const searchStr = `【${title}】`
    const anchor = `<span id="prd-anchor-${index}" style="display:block;height:0;"></span>${searchStr}`
    return result.replace(searchStr, anchor)
  }, content)
}

/** 输出区域主组件 — 管理占位/加载/错误/内容四种展示状态，集成步骤导航、模块导航、复制和滚动控制 */
export default function ResultArea({
  agents,
  activeViewStep,
  onUserStepClick,
  isGenerating,
  quotedInput,
  fullInput,
  onRetry,
}: ResultAreaProps) {
  const activeAgent = agents[activeViewStep]
  const hasAnyContent = agents.some((a) => a.content)
  const showPlaceholder = !hasAnyContent && !isGenerating && !activeAgent?.error

  // 当前查看步骤的内容
  const content = activeAgent?.content || ''
  const hasContent = content.length > 0
  const stepIsGenerating = activeAgent?.status === 'running'
  const stepError = activeAgent?.error || null
  const isPRDStep = activeViewStep === 1

  // 滚动控制（跟踪当前步骤的内容和生成状态）
  const { contentRef, showBackToTop, showScrollToBottom, scrollToTop, scrollToBottom } = useScrollControl(
    stepIsGenerating,
    content
  )

  // 复制按钮状态
  const [copyDone, setCopyDone] = useState(false)

  // 引用条 strong 溢出检测 → 条件 tooltip
  const quoteInputRef = useRef<HTMLElement>(null)
  const [quoteTruncated, setQuoteTruncated] = useState(false)
  useEffect(() => {
    const el = quoteInputRef.current
    if (!el) return
    const check = () => setQuoteTruncated(el.scrollWidth > el.clientWidth)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [quotedInput])

  const handleCopy = useCallback(() => {
    if (!content) return
    navigator.clipboard.writeText(content).then(
      () => {
        setCopyDone(true)
        setTimeout(() => setCopyDone(false), 2000)
      },
      () => {
        // 降级方案
        const textarea = document.createElement('textarea')
        textarea.value = content
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        setCopyDone(true)
        setTimeout(() => setCopyDone(false), 2000)
      }
    )
  }, [content])

  // 构建 HTML 内容
  const htmlContent = useMemo(() => {
    if (!content) return ''
    const processed = isPRDStep ? insertAnchors(content) : content
    return processed
  }, [content, isPRDStep])

  return (
    <div className="main-section">
      {/* 引用条 — 与 result-wrapper 同级 */}
      {(hasAnyContent || isGenerating) && quotedInput && (
        <div className="quote-bar">
          <span className="quote-icon">
            <img src={iconChat} alt="" />
          </span>
          <span className="quote-text">
            <span className="quote-prefix">根据您的需求：&quot;</span>
            <span className={`quote-input-wrapper${quoteTruncated ? ' is-truncated' : ''}`}>
              <strong className="quote-input" ref={quoteInputRef}>
                {quotedInput}
              </strong>
              <span className="quote-tooltip">
                <span className="quote-tooltip-inner">{fullInput}</span>
              </span>
            </span>
            <span className="quote-suffix">&quot; 生成</span>
          </span>
        </div>
      )}

      {/* 步骤导航 — 与 result-wrapper 同级 */}
      {(hasAnyContent || isGenerating) && (
        <StepNavigation agents={agents} activeViewStep={activeViewStep} onStepClick={onUserStepClick} />
      )}

      {/* 内容区域 */}
      <div className="result-wrapper">
        <div className="result-area">
          {/* 占位（初始空状态） */}
          {showPlaceholder && (
            <div className="placeholder">
              <img src={placeholderIllust} className="placeholder-icon" alt="" />
              <span className="placeholder-text">生成的 PRD 将在这里展示</span>
            </div>
          )}

          {/* 错误状态 */}
          {stepError && <ErrorStateComponent error={stepError} onRetry={onRetry} />}

          {/* 内容区（仅在无错误时显示） */}
          {!stepError && (hasContent || stepIsGenerating) && (
            <div className="step-content-wrapper">
              {/* PRD 文档的模块导航（仅 Agent 2 显示） */}
              {isPRDStep && (
                <Navigation contentDivRef={contentRef} content={content} isDone={activeAgent?.status === 'done'} />
              )}

              {hasContent ? (
                <div
                  className={`prd-content fade-in${stepIsGenerating ? ' is-generating' : ''}`}
                  key={activeViewStep}
                  ref={contentRef}
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
              ) : stepIsGenerating ? (
                <div className="prd-content is-generating" ref={contentRef}>
                  <Loading />
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* 复制按钮 */}
        {hasContent && !stepIsGenerating && (
          <div className={`copy-btn ${copyDone ? 'copied' : ''}`} onClick={handleCopy} title="复制内容">
            {copyDone ? (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
          </div>
        )}

        <BackToTop visible={showBackToTop} onClick={scrollToTop} />

        {showScrollToBottom && (
          <div className="scroll-to-bottom-btn" title="滚动到底部" onClick={scrollToBottom}>
            ↓
          </div>
        )}
      </div>
    </div>
  )
}
